import CustomButton from "../../../components/CustomButton";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import SubHeadings from "../../../components/SubHeadings";
import { Staff } from "../../../types/staff";
import { useFormEdit } from "../../../util/hooks/useFormEdit";

interface PersonalInformationProps {
  staff?: Staff;
  onChange: (data: Partial<Staff>) => void;
}

export default function PersonalDetails({
  staff,
  onChange,
}: PersonalInformationProps) {
  const { isEditing } = useFormEdit<Staff>(staff || ({} as Staff));

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;

    let newValue: any = value;

    if (type === "radio") {
      if (value === "true") newValue = true;
      if (value === "false") newValue = false;
    }

    onChange({
      [name]: newValue,
    } as Partial<Staff>);
  };

  return (
    <div className="px-4 md:px-9">
      <div className="flex flex-col gap-7 w-full py-7">
        <SubHeadings text="Personal Details" />
        <form className="grid gap-7">
          <div className="grid gap-7">
            <CustomInput
              name="permanentAddress"
              label="Permanent Address"
              readOnly={!isEditing}
              type="text"
              placeholder="Enter full permanent address"
              value={staff?.permanentAddress || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="communicationAddress"
              label="Address for Communication"
              type="text"
              readOnly={!isEditing}
              placeholder="Enter full communication address"
              value={staff?.communicationAddress || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              <CustomInput
                name="nationality"
                label="Nationality"
                type="text"
                readOnly={!isEditing}
                placeholder="Enter nationality"
                value={staff?.nationality || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />

              <CustomInput
                name="religion"
                label="Religion"
                type="text"
                readOnly={!isEditing}
                placeholder="Enter religion"
                value={staff?.religion || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />

              <CustomInput
                name="aadhaarNumber"
                label="Aadhaar Number"
                type="text"
                readOnly={!isEditing}
                placeholder="Enter Aadhaar number"
                value={staff?.aadhaarNumber || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />

              <CustomInput
                name="fatherName"
                label="Father's Name"
                type="text"
                readOnly={!isEditing}
                placeholder="Enter father's name"
                value={staff?.fatherName || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />

              <CustomInput
                name="motherName"
                label="Mother's Name"
                type="text"
                readOnly={!isEditing}
                placeholder="Enter mother's name"
                value={staff?.motherName || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />

              <CustomInput
                name="maritalStatus"
                label="Marital Status"
                type="text"
                readOnly={!isEditing}
                placeholder="Enter marital status"
                value={staff?.maritalStatus || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />
            </div>

            {/* Employee Info */}
            <div className="grid md:grid-cols-2 gap-7">
              <CustomSelect
                name="employeeType"
                label="Employee Type"
                value={staff.employeeType || ""}
                readOnly={!isEditing}
                options={["Full-time", "Part-time", "Contract"]}
                placeholder="Select employee type"
                onChange={handleInputChange}
              />
              <CustomInput
                name="qualification"
                label="Qualification"
                type="text"
                readOnly={!isEditing}
                placeholder="Enter qualification"
                value={staff.qualification || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />
            </div>

            {/* College & About */}
            <div className="grid gap-7">
              <CustomInput
                name="collegeDetails"
                label="College Details"
                type="text"
                readOnly={!isEditing}
                placeholder="Enter college details"
                value={staff.collegeDetails || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />

              <CustomInput
                name="about"
                label="About"
                type="text"
                readOnly={!isEditing}
                isTextarea
                placeholder="Write something about yourself..."
                value={staff.about || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />
            </div>
          </div>

          <div className="flex gap-7">
            <label className="text-sm font-semibold text-neutral-400 block">
              Appointment letter sent?
            </label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CustomInput
                  name="appointmentLetterSent"
                  type="radio"
                  value="true"
                  checked={staff.appointmentLetterSent === true}
                  disabled={!isEditing}
                  onChange={handleInputChange}
                  inputClassName="w-3 h-3"
                />
                <span className="text-sm text-neutral-300 font-semibold">
                  Yes
                </span>
              </div>

              <div className="flex items-center gap-2">
                <CustomInput
                  name="appointmentLetterSent"
                  type="radio"
                  value="false"
                  checked={staff.appointmentLetterSent === false}
                  disabled={!isEditing}
                  onChange={handleInputChange}
                  inputClassName="w-3 h-3"
                />
                <span className="text-sm text-neutral-300 font-semibold">
                  No
                </span>
              </div>
            </div>
          </div>

          {/* Working Info */}
          <div className="grid md:grid-cols-3 gap-7">
            <CustomSelect
              name="workingDays"
              label="Working Days"
              value={staff.workingDays || ""}
              readOnly={!isEditing}
              options={["Mon-Fri", "Mon-Sat", "Custom"]}
              placeholder="Select working days"
              onChange={handleInputChange}
            />
            <CustomInput
              name="checkInTime"
              label="Check-in Time"
              value={staff.checkInTime || ""}
              readOnly={!isEditing}
              type="time"
              placeholder="Select check-in time"
              onChange={handleInputChange}
            />
            <CustomInput
              name="checkOutTime"
              label="Check-out Time"
              value={staff.checkOutTime || ""}
              readOnly={!isEditing}
              type="time"
              placeholder="Select check-out time"
              onChange={handleInputChange}
            />
          </div>

          <div>
            <CustomInput
              name="remarks"
              label="Remarks"
              type="text"
              readOnly={!isEditing}
              placeholder="Enter remarks (if any)"
              value={staff.remarks || ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
          </div>

          {/* <div className="flex justify-end items-end relative group">
            <CustomButton
              color="bg-primary-500"
              text="Submit"
              disabled={!hasUnsavedChanges || !isEditing}
            />
            {!hasUnsavedChanges && (
              <span className="absolute bottom-full mb-2 right-0 bg-neutral-300 text-white text-xs font-medium px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                {isEditing
                  ? "No changes yet — fill out or edit the form to get started"
                  : "Enable edit mode to make changes"}
              </span>
            )}
          </div> */}
        </form>
      </div>
    </div>
  );
}
